import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';
import { clientEnv } from '@/config/env';
import type { Database } from './types';
import * as Sentry from "@sentry/nextjs";

// ============================================================================
// Browser Client (uses anon key, with cookie-based session storage)
// ============================================================================

let browserClient: SupabaseClient<Database> | null = null;

/**
 * Get or create the browser Supabase client.
 * Uses the anon key - safe for client-side use.
 * Session is stored in cookies (via @supabase/ssr) for SSR compatibility.
 * This ensures Route Handlers can access the auth session from cookies.
 */
export function getBrowserClient(): SupabaseClient<Database> {
  if (typeof window === 'undefined') {
    throw new Error('getBrowserClient should only be called in the browser');
  }

  if (!browserClient) {
    browserClient = createBrowserClient<Database>(
      clientEnv.NEXT_PUBLIC_SUPABASE_URL,
      clientEnv.NEXT_PUBLIC_SUPABASE_ANON_KEY
    );
  }

  return browserClient;
}

// ============================================================================
// Server Client (uses service role key, bypasses RLS)
// ============================================================================

/**
 * Create a server-side Supabase client with service role.
 * ONLY use in Route Handlers, Server Actions, or middleware.
 * This client bypasses RLS - you MUST validate authorization manually.
 */
export function getServerClient(): SupabaseClient<Database> {
  if (typeof window !== 'undefined') {
    throw new Error('getServerClient should only be called on the server');
  }

  // Import server env dynamically to avoid client-side access
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;

  if (!serviceRoleKey || !supabaseUrl) {
    throw new Error('Missing Supabase server environment variables');
  }

  try {
    const client = createClient<Database>(supabaseUrl, serviceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    });

    // Validate client
    if (!client || typeof client.from !== 'function') {
      throw new Error('Supabase client initialization failed - client.from is not a function');
    }

    return client;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Create a server-side Supabase client that uses the user's session.
 * Use this when you want RLS to apply based on the authenticated user.
 * Pass the access token from the request headers or cookies.
 */
export function getServerClientWithAuth(accessToken: string): SupabaseClient<Database> {
  if (typeof window !== 'undefined') {
    throw new Error('getServerClientWithAuth should only be called on the server');
  }

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
    global: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  });
}

// ============================================================================
// Untyped Server Client (bypasses strict TypeScript type checking)
// ============================================================================

/**
 * Create an untyped server-side Supabase client.
 * Use this when TypeScript's strict type checking causes issues with
 * insert/update/upsert operations due to Database type inference problems.
 * This client still uses the service role key but without type constraints.
 */
export function getUntypedServerClient() {
  if (typeof window !== 'undefined') {
    throw new Error('getUntypedServerClient should only be called on the server');
  }

  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;

  if (!serviceRoleKey || !supabaseUrl) {
    const errorMsg = 'Missing Supabase server environment variables';
    Sentry.captureException(new Error(errorMsg));
    throw new Error(errorMsg);
  }

  try {
    const client = createClient(supabaseUrl, serviceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    });

    // CRITICAL: Validate that the client is properly initialized
    if (!client || typeof client.from !== 'function') {
      const errorMsg = 'Supabase client initialization failed - client.from is not a function';
      Sentry.captureException(new Error(errorMsg));
      throw new Error(errorMsg);
    }

    return client;
  } catch (error: any) {
    const errorMsg = `Failed to create Supabase client: ${error.message || 'Unknown error'}`;
    Sentry.captureException(error);
    throw new Error(errorMsg);
  }
}

// ============================================================================
// Session Helpers
// ============================================================================

/**
 * Get the current session from the browser client.
 * Returns null if not authenticated.
 */
export async function getSession() {
  const client = getBrowserClient();
  const { data: { session }, error } = await client.auth.getSession();
  
  if (error) {
    Sentry.captureException(error);
    return null;
  }
  
  return session;
}

/**
 * Get the current user from the browser client.
 * Returns null if not authenticated.
 */
export async function getUser() {
  const client = getBrowserClient();
  const { data: { user }, error } = await client.auth.getUser();
  
  if (error) {
    Sentry.captureException(error);
    return null;
  }
  
  return user;
}

/**
 * Sign out the current user.
 */
export async function signOut() {
  const client = getBrowserClient();
  const { error } = await client.auth.signOut();
  
  if (error) {
    Sentry.captureException(error);
    throw error;
  }
}

